import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, LayersControl, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
const baseUrl =  process.env.REACT_APP_API_URL + "/api/zones";
const DGCAIndiaDroneMap = () => {
  const [showRedZone, setShowRedZone] = useState(true);
  const [showYellowZone, setShowYellowZone] = useState(true);
  const [showGreenZone, setShowGreenZone] = useState(true);
  const [activeTab, setActiveTab] = useState('map');
  const [zones, setZones] = useState({
    red: null,
    yellow: null,
    green: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // India boundaries
  const INDIA_BOUNDS = [
    [6.4627, 68.1097],
    [35.501, 97.3953]
  ];

  // DGCA API configuration
  const DGCA_API_ENDPOINT = 'https://digitalsky.dgca.gov.in/anchor_proxy_api_v2_1/service.php';
  const API_TOKEN = 'U2FsdGVkX18DKI1QZXl1K2cq3Y30n0Z5tRnLzFv3TZ7EiznCuFkfZ87EUA9rZthO8cz9hSoLXMujLh1i7qd77UGgaWPSffTOzYZGS5H/KIOCUQApoHV1IlT4/0 rh2sGy';

  // Fetch zone data from DGCA API
  useEffect(() => {
    const fetchZoneData = async () => {
      try {
        setLoading(true);
        
        const headers = {
          'Authorization': `Bearer ${API_TOKEN}`,
          'Content-Type': 'application/json'
        };

        // Fetch all zones in parallel
      const [redResponse, yellowResponse, greenResponse] = await Promise.all([
  axios.get(`${baseUrl}/red`),
  axios.get(`${baseUrl}/yellow`),
  axios.get(`${baseUrl}/green`)
]);

        setZones({
          red: redResponse.data,
          yellow: yellowResponse.data,
          green: greenResponse.data
        });
        
      } catch (err) {
  console.error('Error fetching DGCA zone data:', err);
  setError('Failed to load zone data. Please try again later.');
  setZones({
    red: null,
    yellow: null,
    green: null
  });
}
finally {
        setLoading(false);
      }
    };

    fetchZoneData();
  }, []);

  // Zone styles matching DGCA standards
  const redZoneStyle = {
    fillColor: '#ff0000',
    weight: 2,
    opacity: 1,
    color: '#ff0000',
    fillOpacity: 0.2,
    dashArray: '5, 5'
  };

  const yellowZoneStyle = {
    fillColor: '#ffff00',
    weight: 2,
    opacity: 1,
    color: '#ffcc00',
    fillOpacity: 0.2,
    dashArray: '5, 5'
  };

  const greenZoneStyle = {
    fillColor: '#00ff00',
    weight: 2,
    opacity: 1,
    color: '#00cc00',
    fillOpacity: 0.2
  };

  // Enhanced DGCA popup
  const onEachFeature = (feature, layer) => {
    if (feature.properties) {
      const popupContent = `
        <div class="dgca-popup">
          <h4>${feature.properties.name || 'DGCA Zone'}</h4>
          <p><strong>Type:</strong> ${feature.properties.zone_type || 'Unknown'}</p>
          <p><strong>Category:</strong> ${feature.properties.category || 'N/A'}</p>
          <p><strong>Restrictions:</strong> ${feature.properties.restrictions || 'See DGCA regulations'}</p>
          ${feature.properties.valid_until ? `<p><strong>Valid Until:</strong> ${new Date(feature.properties.valid_until).toLocaleDateString()}</p>` : ''}
          <div class="dgca-notice">
            <small>DGCA Drone Regulations 2021</small>
          </div>
        </div>
      `;
      layer.bindPopup(popupContent);
    }
  };

  // Fix Leaflet icons
  useEffect(() => {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
      iconUrl: require('leaflet/dist/images/marker-icon.png'),
      shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    });
  }, []);

  return (
    <div className="dgca-container">
      <header className="dgca-header">
        <img src="https://dgca.gov.in/digigov-portal/img/dgca-logo.png" alt="DGCA Logo" className="logo" />
        <div className="header-content">
          <h1>Digital Sky India - Drone Flying Zones</h1>
          <p>Official no-fly zones and approved areas across India</p>
        </div>
      </header>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'map' ? 'active' : ''}`}
          onClick={() => setActiveTab('map')}
        >
          Interactive Map
        </button>
        <button 
          className={`tab ${activeTab === 'regulations' ? 'active' : ''}`}
          onClick={() => setActiveTab('regulations')}
        >
          DGCA Regulations
        </button>
        <button 
          className={`tab ${activeTab === 'permissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('permissions')}
        >
          Flight Permissions
        </button>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading DGCA zone data...</p>
        </div>
      )}

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
        </div>
      )}

      {activeTab === 'map' && (
        <div className="map-section">
          <div className="controls-panel">
            <h3>Zone Controls</h3>
            <div className="toggle-group">
              <label className="toggle red">
                <input 
                  type="checkbox" 
                  checked={showRedZone} 
                  onChange={() => setShowRedZone(!showRedZone)} 
                />
                <span className="toggle-slider"></span>
                Red Zones (No-Fly)
              </label>
              <label className="toggle yellow">
                <input 
                  type="checkbox" 
                  checked={showYellowZone} 
                  onChange={() => setShowYellowZone(!showYellowZone)} 
                />
                <span className="toggle-slider"></span>
                Yellow Zones (Restricted)
              </label>
              <label className="toggle green">
                <input 
                  type="checkbox" 
                  checked={showGreenZone} 
                  onChange={() => setShowGreenZone(!showGreenZone)} 
                />
                <span className="toggle-slider"></span>
                Green Zones (Approved)
              </label>
            </div>
          </div>

          <div className="map-container">
            <MapContainer 
              center={[20.5937, 78.9629]} 
              zoom={5}
              minZoom={4}
              maxBounds={INDIA_BOUNDS}
              style={{ height: '100%', width: '100%' }}
              zoomControl={false}
            >
              <ZoomControl position="topright" />
              
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="Standard">
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Satellite">
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution="Tiles &copy; Esri"
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Terrain">
                  <TileLayer
                    url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
                  />
                </LayersControl.BaseLayer>
              </LayersControl>

              {showRedZone && zones.red && (
                <GeoJSON 
                  data={zones.red} 
                  style={redZoneStyle} 
                  onEachFeature={onEachFeature}
                />
              )}

              {showYellowZone && zones.yellow && (
                <GeoJSON 
                  data={zones.yellow} 
                  style={yellowZoneStyle} 
                  onEachFeature={onEachFeature}
                />
              )}

              {showGreenZone && zones.green && (
                <GeoJSON 
                  data={zones.green} 
                  style={greenZoneStyle} 
                  onEachFeature={onEachFeature}
                />
              )}
            </MapContainer>
          </div>

          <div className="legend">
            <h4>DGCA Zone Legend</h4>
            <div className="legend-items">
              <div className="legend-item">
                <div className="color-swatch red"></div>
                <div className="legend-text">
                  <strong>Red Zone</strong>
                  <p>No drone flights permitted (airports, military, etc.)</p>
                </div>
              </div>
              <div className="legend-item">
                <div className="color-swatch yellow"></div>
                <div className="legend-text">
                  <strong>Yellow Zone</strong>
                  <p>Restricted - Requires ATC permission</p>
                </div>
              </div>
              <div className="legend-item">
                <div className="color-swatch green"></div>
                <div className="legend-text">
                  <strong>Green Zone</strong>
                  <p>Approved for drone flights up to 400ft</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'regulations' && (
        <div className="regulations-section">
          <h2>DGCA Drone Regulations 2021</h2>
          
          <div className="regulation-card">
            <h3>Classification of Zones</h3>
            <ul>
              <li><strong>Red Zone:</strong> No drone flights permitted under any circumstances</li>
              <li><strong>Yellow Zone:</strong> Requires air traffic control clearance</li>
              <li><strong>Green Zone:</strong> Automatic permission via Digital Sky</li>
            </ul>
          </div>

          <div className="regulation-card">
            <h3>Operational Requirements</h3>
            <ul>
              <li>All drones (except nano category) must be registered</li>
              <li>Maximum altitude of 400 feet (120m) in green zones</li>
              <li>Visual line of sight (VLOS) operations only</li>
              <li>No night operations without special permission</li>
            </ul>
          </div>

          <div className="regulation-card">
            <h3>Penalties for Violations</h3>
            <table>
              <thead>
                <tr>
                  <th>Violation</th>
                  <th>Penalty</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Flying in red zone</td>
                  <td>₹50,000 fine and/or imprisonment</td>
                </tr>
                <tr>
                  <td>Flying without permission</td>
                  <td>₹25,000 fine</td>
                </tr>
                <tr>
                  <td>Unregistered drone</td>
                  <td>₹10,000 fine</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="external-links">
            <a href="https://dgca.gov.in/digigov-portal/" target="_blank" rel="noopener noreferrer" className="dgca-link">
              Official DGCA Website
            </a>
            <a href="https://digitalsky.dgca.gov.in/" target="_blank" rel="noopener noreferrer" className="digitalsky-link">
              Digital Sky Portal
            </a>
          </div>
        </div>
      )}

      {activeTab === 'permissions' && (
        <div className="permissions-section">
          <h2>Drone Flight Permission Process</h2>
          
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Register Your Drone</h3>
                <p>All drones (except nano category) must be registered on Digital Sky platform</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Check Zone Classification</h3>
                <p>Verify your intended flight location on this map</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Apply for Permission</h3>
                <p>Submit flight plan through Digital Sky portal</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Receive Clearance</h3>
                <p>Green zones: Instant approval<br />
                Yellow zones: ATC clearance required</p>
              </div>
            </div>
          </div>

          <div className="permission-actions">
            <button className="primary-button">
              Apply for Flight Permission
            </button>
            <button className="secondary-button">
              Check Application Status
            </button>
          </div>
        </div>
      )}

      <footer className="dgca-footer">
        <p>© 2023 Directorate General of Civil Aviation, India</p>
        <p>This map is for informational purposes only. Always verify with official DGCA sources.</p>
      </footer>

      <style jsx>{`
        .dgca-container {
          max-width: 1200px;
          margin: 0 auto;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          background: #f8fafc;
          min-height: 100vh;
        }

        .dgca-header {
          background: linear-gradient(135deg, #1a3e72 0%, #0a1f3d 100%);
          color: white;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .logo {
          height: 70px;
        }

        .header-content h1 {
          margin: 0;
          font-size: 24px;
        }

        .header-content p {
          margin: 5px 0 0;
          opacity: 0.9;
        }

        .tabs {
          display: flex;
          border-bottom: 1px solid #ddd;
          padding: 0 20px;
        }

        .tab {
          padding: 12px 20px;
          background: none;
          border: none;
          border-bottom: 3px solid transparent;
          font-weight: 600;
          color: #4a5568;
          cursor: pointer;
          transition: all 0.3s;
        }

        .tab.active {
          color: #2b6cb0;
          border-bottom-color: #2b6cb0;
        }

        .tab:hover {
          color: #2b6cb0;
        }

        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.8);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .spinner {
          border: 5px solid #f3f3f3;
          border-top: 5px solid #2b6cb0;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-banner {
          background: #fff5f5;
          color: #c53030;
          padding: 10px 20px;
          border-left: 4px solid #c53030;
          margin: 10px 20px;
        }

        .map-section {
          display: grid;
          grid-template-columns: 250px 1fr;
          gap: 20px;
          padding: 20px;
        }

        .controls-panel {
          background: white;
          padding: 15px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .controls-panel h3 {
          margin-top: 0;
          color: #2d3748;
          font-size: 16px;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 10px;
        }

        .toggle-group {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .toggle {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          position: relative;
          padding-left: 50px;
          min-height: 24px;
        }

        .toggle input {
          position: absolute;
          opacity: 0;
          cursor: pointer;
          height: 0;
          width: 0;
        }

        .toggle-slider {
          position: absolute;
          left: 0;
          top: 0;
          width: 40px;
          height: 24px;
          background-color: #ccc;
          border-radius: 12px;
          transition: .4s;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 4px;
          bottom: 4px;
          background-color: white;
          border-radius: 50%;
          transition: .4s;
        }

        input:checked + .toggle-slider {
          background-color: #38a169;
        }

        input:checked + .toggle-slider:before {
          transform: translateX(16px);
        }

        .red input:checked + .toggle-slider {
          background-color: #e53e3e;
        }

        .yellow input:checked + .toggle-slider {
          background-color: #d69e2e;
        }

        .map-container {
          height: 600px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .legend {
          grid-column: 1 / -1;
          background: white;
          padding: 15px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .legend h4 {
          margin-top: 0;
          color: #2d3748;
        }

        .legend-items {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .legend-item {
          display: flex;
          gap: 10px;
          align-items: flex-start;
          flex: 1;
          min-width: 250px;
        }

        .color-swatch {
          width: 20px;
          height: 20px;
          border-radius: 4px;
          margin-top: 3px;
          flex-shrink: 0;
        }

        .legend-text {
          flex-grow: 1;
        }

        .legend-text strong {
          display: block;
          margin-bottom: 2px;
        }

        .legend-text p {
          margin: 0;
          font-size: 14px;
          color: #4a5568;
        }

        .red { background: #e53e3e; }
        .yellow { background: #d69e2e; }
        .green { background: #38a169; }

        .regulations-section {
          padding: 20px;
        }

        .regulations-section h2 {
          color: #2d3748;
          margin-top: 0;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 10px;
        }

        .regulation-card {
          background: white;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .regulation-card h3 {
          color: #2b6cb0;
          margin-top: 0;
        }

        .regulation-card ul {
          padding-left: 20px;
        }

        .regulation-card li {
          margin-bottom: 8px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 15px;
        }

        th, td {
          padding: 10px;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
        }

        th {
          background: #f7fafc;
          font-weight: 600;
        }

        .external-links {
          display: flex;
          gap: 15px;
          margin-top: 30px;
        }

        .dgca-link, .digitalsky-link {
          display: inline-block;
          padding: 12px 20px;
          border-radius: 6px;
          text-decoration: none;
          font-weight: 500;
          transition: all 0.3s;
        }

        .dgca-link {
          background: #2b6cb0;
          color: white;
        }

        .digitalsky-link {
          background: #38a169;
          color: white;
        }

        .dgca-link:hover, .digitalsky-link:hover {
          opacity: 0.9;
          transform: translateY(-2px);
        }

        .permissions-section {
          padding: 20px;
        }

        .permissions-section h2 {
          color: #2d3748;
          margin-top: 0;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 10px;
        }

        .steps {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin: 30px 0;
        }

        .step {
          display: flex;
          gap: 20px;
          align-items: flex-start;
        }

        .step-number {
          background: #2b6cb0;
          color: white;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          flex-shrink: 0;
        }

        .step-content h3 {
          margin: 0 0 5px 0;
          color: #2d3748;
        }

        .step-content p {
          margin: 0;
          color: #4a5568;
        }

        .permission-actions {
          display: flex;
          gap: 15px;
          margin-top: 40px;
        }

        .primary-button, .secondary-button {
          padding: 12px 24px;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s;
        }

        .primary-button {
          background: #2b6cb0;
          color: white;
          border: none;
        }

        .secondary-button {
          background: white;
          color: #2b6cb0;
          border: 1px solid #2b6cb0;
        }

        .primary-button:hover, .secondary-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }

        .dgca-footer {
          background: #2d3748;
          color: white;
          padding: 20px;
          text-align: center;
          margin-top: 40px;
        }

        .dgca-footer p {
          margin: 5px 0;
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .dgca-header {
            flex-direction: column;
            text-align: center;
          }
          
          .map-section {
            grid-template-columns: 1fr;
          }
          
          .tabs {
            overflow-x: auto;
          }
          
          .external-links, .permission-actions {
            flex-direction: column;
          }
        }
      `}</style>

      <style global jsx>{`
        .dgca-popup {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          min-width: 250px;
          max-width: 300px;
        }
        
        .dgca-popup h4 {
          margin: 0 0 10px 0;
          color: #2d3748;
          font-size: 16px;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 5px;
        }
        
        .dgca-popup p {
          margin: 0 0 8px 0;
          color: #4a5568;
          font-size: 14px;
          line-height: 1.4;
        }
        
        .dgca-popup strong {
          color: #2d3748;
        }
        
        .dgca-notice {
          margin-top: 10px;
          padding-top: 8px;
          border-top: 1px dashed #e2e8f0;
          color: #718096;
          font-size: 12px;
        }
        
        .leaflet-control-layers {
          border-radius: 4px !important;
          overflow: hidden !important;
          box-shadow: 0 1px 5px rgba(0,0,0,0.1) !important;
        }
        
        .leaflet-control-layers label {
          padding: 8px 15px !important;
          margin: 0 !important;
        }
        
        .leaflet-control-layers label:hover {
          background: #f7fafc !important;
        }
        
        .leaflet-control-zoom {
          border-radius: 4px !important;
          overflow: hidden !important;
        }
      `}</style>
    </div>
  );
};

export default DGCAIndiaDroneMap;